import React, { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip } from "antd";
import dayjs from "dayjs";
import { MemberSearchResult } from "../../model/members/MemberSearchResult";
import { fromDateOnly } from "../../utils/DateOnly";
import { useMemberService } from "../../services/members/MemberServiceProvider";

// Shared building blocks for every member-search surface (the main search bar and
// the booking / playing-member selectors) so the look, feel and behaviour stay
// identical. Matching and ranking live in Postgres (GET /members/search); this
// module only handles presentation (status colour-dot, option row) and the
// debounced fetch hook.

export type MemberStatusTone = "active" | "expired" | "pending";

export interface MemberStatusInfo {
    tone: MemberStatusTone;
    color: string;
    tooltip: string;
}

// Ant Design's success / error / warning palette.
export const MEMBER_STATUS_COLOURS: Record<MemberStatusTone, string> = {
    active: "#52c41a",
    expired: "#ff4d4f",
    pending: "#faad14",
};

const capitalise = (value: string): string =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

// validTo is DateOnly on the wire, so DateReviver hands us a Date at UTC midnight —
// read with dayjs() directly that names the previous day in any timezone behind UTC,
// and the dot turns red (and the tooltip names the wrong day) while the membership is
// still valid. fromDateOnly reads its UTC calendar fields; raw ISO strings pass through.
const toDay = (validTo?: Date | string | null) => {
    if (!validTo) return null;
    const d = fromDateOnly(validTo);
    return d.isValid() ? d : null;
};

/**
 * Green = active member (Active status and not past its valid-to date).
 * Red = expired (valid-to in the past) or a terminated status (Cancelled/Banned).
 * Amber = any other non-active state (Pending, Inactive, Suspended, unknown).
 */
export function getMemberStatusInfo(status?: string, validTo?: Date | string | null): MemberStatusInfo {
    const normalised = (status ?? "").trim().toLowerCase();
    const validToDay = toDay(validTo);
    const isPastValidTo = validToDay != null && validToDay.endOf("day").isBefore(dayjs());
    const isTerminated = normalised === "cancelled" || normalised === "expired" || normalised === "lapsed" || normalised === "banned";

    if (isPastValidTo || isTerminated) {
        const tooltip = validToDay
            ? `Expired on ${validToDay.format("D MMM YYYY")}`
            : capitalise(normalised) || "Expired";
        return { tone: "expired", color: MEMBER_STATUS_COLOURS.expired, tooltip };
    }

    if (normalised === "active") {
        return { tone: "active", color: MEMBER_STATUS_COLOURS.active, tooltip: "Active member" };
    }

    return { tone: "pending", color: MEMBER_STATUS_COLOURS.pending, tooltip: capitalise(normalised) || "Unknown status" };
}

interface MemberStatusDotProps {
    status?: string;
    validTo?: Date | string | null;
    size?: number;
}

/** Small colour-coded circle with a status tooltip on hover. */
export const MemberStatusDot: React.FC<MemberStatusDotProps> = ({ status, validTo, size = 10 }) => {
    const info = getMemberStatusInfo(status, validTo);
    return (
        <Tooltip title={info.tooltip}>
            <span
                role="img"
                aria-label={info.tooltip}
                style={{
                    display: "inline-block",
                    width: size,
                    height: size,
                    borderRadius: "50%",
                    backgroundColor: info.color,
                    flexShrink: 0,
                }}
            />
        </Tooltip>
    );
};

/** Secondary line under a member's name: id | Legacy | email | phone. */
export const buildMemberSecondaryLine = (member: MemberSearchResult): string =>
    [
        member.membershipId,
        member.legacyMembershipId ? `Legacy: ${member.legacyMembershipId}` : null,
        member.emailAddress,
        member.phoneNumber,
    ]
        .filter(Boolean)
        .join(" | ");

/** A single dropdown row: status dot + name + contact line. */
export const MemberOptionLabel: React.FC<{ member: MemberSearchResult }> = ({ member }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 0" }}>
        <MemberStatusDot status={member.status} validTo={member.validTo} />
        <div style={{ minWidth: 0 }}>
            <div>{member.name}</div>
            <div style={{ fontSize: "0.8em", color: "#888" }}>{buildMemberSecondaryLine(member)}</div>
        </div>
    </div>
);

export interface UseMemberSearch {
    results: MemberSearchResult[];
    loading: boolean;
    /** Debounced server-side search; clears results for terms under two chars. */
    search: (term: string) => void;
    /** Cancel any in-flight debounce and clear the results. */
    reset: () => void;
}

const MIN_SEARCH_LENGTH = 2;

/**
 * Debounced server-side member search shared by every search surface. Results
 * arrive already ranked from the backend, so `results[0]` is the best match
 * (drives Enter-to-select). A request-id guard drops out-of-order responses so a
 * slow earlier keystroke can't overwrite a newer result set.
 *
 * When `activeOnly` is true the backend restricts hits to currently-active
 * members (Active status, in-date, enabled). Toggling it re-runs the search for
 * the current term immediately (no debounce — it's a deliberate click, not a
 * keystroke), so the visible list refreshes without the operator retyping.
 */
export function useMemberSearch(activeOnly: boolean = false, debounceMs: number = 250): UseMemberSearch {
    const memberService = useMemberService();
    const [results, setResults] = useState<MemberSearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const latestRequestId = useRef(0);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();
    // Read inside runSearch so an already-scheduled debounce picks up the latest
    // toggle value rather than the one captured when the timer was set.
    const activeOnlyRef = useRef(activeOnly);
    // The last term we searched, so an activeOnly toggle can re-run it.
    const lastTermRef = useRef("");

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
        }
    }, []);

    const runSearch = useCallback(
        async (term: string) => {
            const requestId = ++latestRequestId.current;
            try {
                const hits = await memberService.searchMembers(term, undefined, activeOnlyRef.current);
                if (requestId === latestRequestId.current) setResults(hits);
            } catch {
                if (requestId === latestRequestId.current) setResults([]);
            } finally {
                if (requestId === latestRequestId.current) setLoading(false);
            }
        },
        [memberService]
    );

    useEffect(() => () => clearTimer(), [clearTimer]);

    // Keep the ref current and, when the toggle flips with a live term on screen,
    // refresh the results straight away.
    useEffect(() => {
        activeOnlyRef.current = activeOnly;
        const term = lastTermRef.current;
        if (term.length >= MIN_SEARCH_LENGTH) {
            // Cancel any pending keystroke debounce so we don't fire twice.
            clearTimer();
            setLoading(true);
            runSearch(term);
        }
        // Only react to a genuine toggle change, not to runSearch identity.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeOnly]);

    const search = useCallback(
        (term: string) => {
            clearTimer();
            const trimmed = (term ?? "").trim();
            lastTermRef.current = trimmed;
            if (trimmed.length < MIN_SEARCH_LENGTH) {
                // Bump the request id so any in-flight response is ignored.
                latestRequestId.current++;
                setResults([]);
                setLoading(false);
                return;
            }
            setLoading(true);
            timerRef.current = setTimeout(() => runSearch(trimmed), debounceMs);
        },
        [clearTimer, runSearch, debounceMs]
    );

    const reset = useCallback(() => {
        clearTimer();
        latestRequestId.current++;
        lastTermRef.current = "";
        setResults([]);
        setLoading(false);
    }, [clearTimer]);

    return { results, loading, search, reset };
}
